import React, { useRef, useEffect, forwardRef } from 'react';
import { formatDate, formatBirthdayWithAge } from '@repo/common';
import {
  formatDescriptionToDots,
  getCareerLevelLabel,
  getLocationCountry,
  hasValue,
} from '@repo/common';

const PreviewContent = forwardRef(
  (
    {
      resumeData,
      pageHeight = 551.63,
      setTotalPages,
      currentPage,
      isCreateSuccessPage,
    },
    ref,
  ) => {
    const {
      profile,
      visa,
      summary,
      education,
      experience,
      skill,
      activity,
      language,
      link,
    } = resumeData;
    const containerRef = useRef(null);
    const koreanName = Array.isArray(profile?.koreanName)
      ? profile.koreanName.join(' ')
      : typeof profile?.koreanName === 'string'
        ? profile.koreanName
        : '';

    const PAGE_PADDING_TOP = 18;
    const PAGE_PADDING_BOTTOM = 18;
    const effectivePageHeight =
      pageHeight - PAGE_PADDING_TOP - PAGE_PADDING_BOTTOM;

    useEffect(() => {
      if (!containerRef.current) return;

      const blocks = containerRef.current.querySelectorAll(
        '.break-inside-avoid',
      );

      blocks.forEach((block) => (block.style.marginTop = '0px'));

      blocks.forEach((block) => {
        const blockTop = block.offsetTop - PAGE_PADDING_TOP;
        const blockHeight = block.offsetHeight;

        const startPage = Math.floor(blockTop / effectivePageHeight);
        const endPage = Math.floor(
          (blockTop + blockHeight - 1) / effectivePageHeight,
        );

        if (startPage !== endPage) {
          const nextPageStart =
            (startPage + 1) * effectivePageHeight + PAGE_PADDING_TOP;
          const gap = nextPageStart - blockTop;
          block.style.marginTop = `${gap}px`;
        }
      });

      if (setTotalPages) {
        setTotalPages(
          Math.ceil(containerRef.current.scrollHeight / pageHeight),
        );
      }
    }, [effectivePageHeight, pageHeight, resumeData, setTotalPages]);

    if (!resumeData) return null;

    return (
      <div
        ref={ref}
        className={`flex flex-col w-full  ${isCreateSuccessPage ? 'w-[274px] h-[387px]' : 'max-w-[348px] min-h-[340.88px] max-h-[552px]'} aspect-[390/551] bg-grayscale-gray00  
                overflow-hidden relative shadow-2`}
      >
        <div
          ref={containerRef}
          style={{
            transform: `translateY(-${
              (currentPage - 1) * effectivePageHeight
            }px)`,
            transition: 'transform 0.3s',
          }}
        >
          <div className='px-[17.57px] py-[14.06px]'>
            {/* Header */}
            {profile?.career && (
              <div className='flex justify-end pb-2'>
                <div className='relative w-fit text-right text-[8.2px] leading-[1.4] tracking-[0.22em] font-medium text-text-primary'>
                  {Array.isArray(profile.targetRole) &&
                  profile.targetRole.length > 0
                    ? `${profile.targetRole.join(', ')}, ${getCareerLevelLabel(
                        profile.career,
                      )}`
                    : getCareerLevelLabel(profile.career)}

                  <div className='absolute right-0 -left-[40px] top-full border-[0.66px] border-grayscale-gray10' />
                </div>
              </div>
            )}

            {/* LEFT */}
            <div className='flex h-full w-full'>
              {/* Profile */}
              <div className='flex-[1.9] flex flex-col pr-[13.47px] max-w-[230px] min-w-0'>
                <div className='break-inside-avoid'>
                  {profile && (
                    <div>
                      {(koreanName ||
                        profile.lastName ||
                        profile.firstName) && (
                        <div>
                          <div className='text-[8.2px]'>{koreanName}</div>
                          <div
                            className={`text-[10.54px] font-bold ${
                              koreanName ? '' : 'pt-[2.56px]'
                            }`}
                          >
                            {profile.firstName}, {profile.lastName}
                          </div>
                        </div>
                      )}

                      {(profile.location || profile.koreanSkills) && (
                        <div className='flex flex-wrap items-center content-start gap-1'>
                          {profile.location && (
                            <span className='px-1 py-0.5 bg-grayscale-gray01 text-[5.27px] rounded text-grayscale-gray06'>
                              Based in {getLocationCountry(profile.location)}
                            </span>
                          )}
                          {profile.koreanSkills && (
                            <span className='px-1 py-0.5 bg-grayscale-gray01 text-[5.27px] rounded text-grayscale-gray06'>
                              {profile.topik
                                ? profile.topik.level.split(' (')[0]
                                : profile.koreanSkills}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Profile */}
                {(hasValue(profile?.birthday) ||
                  hasValue(profile?.nationality) ||
                  hasValue(profile?.phone?.number) ||
                  hasValue(profile?.email)) && (
                  <span className='break-inside-avoid'>
                    <span className='text-[8.2px] leading-[1.2] tracking-[0.01em] font-medium inline-block bg-[linear-gradient(transparent,transparent_50%,#1DC37B_50%)]'>
                      Profile
                    </span>
                    <div className='text-[5.85px] leading-[1.4] font-regular text-text-primary pb-[4.69px]'>
                      {profile.birthday && (
                        <p>{formatBirthdayWithAge(profile.birthday)}</p>
                      )}
                      {hasValue(profile?.nationality) && (
                        <p>
                          Nationality{' '}
                          {Array.isArray(profile.nationality)
                            ? profile.nationality.join(', ')
                            : profile.nationality}
                        </p>
                      )}
                      {Array.isArray(profile.phone) &&
                        profile.phone.length > 0 && (
                          <p>
                            Contact{' '}
                            {profile.phone
                              .map((p) => `${p.countryCode} ${p.number}`)
                              .join(', ')}
                          </p>
                        )}
                      {hasValue(profile?.email) && (
                        <p>
                          Email{' '}
                          {Array.isArray(profile.email)
                            ? profile.email.join(', ')
                            : profile.email}
                        </p>
                      )}
                    </div>
                  </span>
                )}

                <div className='flex flex-col gap-[9.37px]'>
                  {/* Visa */}
                  {visa?.length > 0 &&
                    visa.map((v, idx) => {
                      const isNoVisa = v.visaType?.startsWith('No');

                      return (
                        <div
                          key={idx}
                          className={`flex flex-col ${
                            isNoVisa ? 'gap-0' : 'gap-2'
                          }`}
                        >
                          <div className='w-fit flex items-center bg-grayscale-gray01 px-[9.37px] py-[3.51px] rounded break-inside-avoid'>
                            <div className='flex flex-col items-center justify-center pr-1'>
                              <div className='bg-green-deep02 text-green-light02 px-2 py-0.5 rounded flex items-center justify-center text-[6px] font-medium leading-[1.4]'>
                                {isNoVisa
                                  ? `${v.visaType} ${v.visaTypeText}`
                                  : v.visaType}
                              </div>

                              {!isNoVisa && (
                                <span className='text-[6px] text-grayscale-gray06 mt-1 whitespace-nowrap'>
                                  {v.visaTypeText} visa
                                </span>
                              )}
                            </div>

                            {!isNoVisa && v.expiryDate && (
                              <span className='flex items-center gap-1 ml-2'>
                                <span className='text-[5.27px] font-regular leading-[1.4] text-grayscale-gray06'>
                                  Date of Expiry
                                </span>
                                <span className='text-[5.86px] font-medium leading-[1.4] text-text-primary'>
                                  {formatDate(v.expiryDate)}
                                </span>
                              </span>
                            )}

                            {isNoVisa && (
                              <span className='flex items-center gap-1 ml-2'>
                                <span className='text-[6px] font-regular leading-[1.4] text-grayscale-gray06'>
                                  No current Korean visa
                                </span>
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}

                  {/* Summary */}
                  {hasValue(summary) && (
                    <div className='break-inside-avoid'>
                      <div className='text-[8.2px] leading-[1.4] tracking-[0.22em] font-medium text-text-primary pb-[2.63px]'>
                        SUMMARY
                      </div>
                      <div className='flex'>
                        <div className='border-l-grayscale-gray10 border-l-[0.59px]' />
                        <div className='flex items-start pl-[5.86px] max-w-full'>
                          <p className='text-[5.86px] leading-[1.4] tracking-[0] font-regular whitespace-pre-line break-words max-w-full'>
                            {summary}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Experience */}
                  {experience?.length > 0 && (
                    <section className='break-inside-avoid'>
                      <div className='text-[8.2px] leading-[1.4] tracking-[0.22em] font-medium text-text-primary pb-[4.69px] break-inside-avoid'>
                        WORK EXPERIENCE
                      </div>
                      <div className='flex flex-row items-center pb-[2.34px] gap-[11.72px]'>
                        <div className='w-[45px] border-b-[0.59px] border-grayscale-gray10' />
                        <div className='flex-1 border-b-[0.59px] border-green-deep03' />
                      </div>
                      <div className='flex flex-col items-start p-0 gap-[11.72px] overflow-hidden'>
                        {experience.map((exp, idx) => (
                          <div
                            key={idx}
                            className='flex gap-[11.72px] w-full overflow-hidden break-inside-avoid'
                          >
                            <div className='flex-shrink-0 w-[45px] flex flex-col text-[5.86px] leading-[1.4] tracking-[0] text-text-primary gap-0.5'>
                              {exp.employmentPeriod && (
                                <div className='font-regular'>
                                  {exp.employmentPeriod}
                                </div>
                              )}
                              {exp.company && (
                                <div className='font-medium'>{exp.company}</div>
                              )}
                              {exp.jobTitle && (
                                <div className='font-medium italic text-text-secondary'>
                                  {exp.jobTitle}
                                </div>
                              )}
                              {exp.employmentType && (
                                <div className='font-regular'>
                                  {exp.employmentType}
                                </div>
                              )}
                            </div>

                            <div className='flex flex-col flex-1 min-w-0 overflow-hidden gap-[2.34px]'>
                              {exp.headline && (
                                <div className='break-inside-avoid w-fit text-[5.86px] leading-[1.4] tracking-[0] font-medium text-text-primary inline-block bg-[linear-gradient(transparent,transparent_50%,#4DD5A3_50%)]'>
                                  {exp.headline}
                                </div>
                              )}

                              {exp.keyAchievements?.length > 0 && (
                                <div className='list-disc list-inside break-words text-text-primary'>
                                  {exp.keyAchievements.map((ach, achIdx) => (
                                    <div
                                      key={achIdx}
                                      className='break-inside-avoid mb-[1.17px]'
                                    >
                                      {ach.title && (
                                        <div className='text-[5.86px] leading-[1.4] tracking-[0] font-medium'>
                                          {ach.title}
                                        </div>
                                      )}

                                      {ach.description && (
                                        <ul className='list-none p-0 m-0'>
                                          {formatDescriptionToDots(
                                            ach.description,
                                          ).map((line, idx) => (
                                            <li
                                              key={idx}
                                              className='flex items-start text-[4.98px] leading-[1.4] tracking-[-0.03em] font-regular break-words'
                                            >
                                              <span className='mr-[1px] flex-shrink-0'>
                                                •
                                              </span>
                                              <span className='flex-1 min-w-0 break-words'>
                                                {line}
                                              </span>
                                            </li>
                                          ))}
                                        </ul>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </section>
                  )}

                  {/* Activity */}
                  {activity?.length > 0 && (
                    <section className='break-inside-avoid'>
                      <div className='text-[8.2px] leading-[1.4] tracking-[0.22em] font-medium text-text-primary pb-[4.69px] break-inside-avoid'>
                        ACTIVITIES
                      </div>
                      <div className='flex flex-row items-center pb-[2.34px] gap-[11.72px]'>
                        <div className='w-[45px] border-b-[0.59px] border-grayscale-gray10' />
                        <div className='flex-1 border-b-[0.59px] border-green-deep03' />
                      </div>
                      <div className='flex flex-col items-start p-0 gap-[11.72px] overflow-hidden'>
                        {activity.map((act, idx) => (
                          <div
                            key={idx}
                            className='flex gap-[11.72px] w-full overflow-hidden text-[5.86px] leading-[1.4] tracking-[0] text-text-primary break-inside-avoid'
                          >
                            <div className='flex-shrink-0 w-[45px] flex flex-col'>
                              <div className='font-regular'>{act.date}</div>
                            </div>
                            <div className='flex flex-col flex-1 min-w-0 overflow-hidden'>
                              <div className='font-medium mb-[1px] truncate'>
                                {act.title}
                              </div>
                              <div className='font-regular overflow-hidden'>
                                <ul className='list-none p-0 m-0'>
                                  {formatDescriptionToDots(act.description).map(
                                    (line, idx) => (
                                      <li
                                        key={idx}
                                        className='flex items-start leading-[1.4] tracking-[0] font-regular'
                                      >
                                        <span className='mr-[1px] flex-shrink-0'>
                                          •
                                        </span>
                                        <span className='break-words flex-1 min-w-0'>
                                          {line}
                                        </span>
                                      </li>
                                    ),
                                  )}
                                </ul>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </section>
                  )}
                </div>
              </div>

              {/* RIGHT */}
              <div className='flex-1 flex flex-col max-w-[114.28px] min-w-0 '>
                {/* Profile Image */}
                <div className='pb-[16.4px]'>
                  {profile?.profileUrl && (
                    <div className='w-full h-[117.34px] bg-grayscale-gray02 overflow-hidden'>
                      <img
                        src={profile.profileUrl}
                        alt='profile'
                        className='w-full h-full object-cover'
                      />
                    </div>
                  )}
                </div>

                {/* Education */}
                <div className='flex flex-col gap-[9.37px]'>
                  {education?.length > 0 && (
                    <section>
                      <div className='text-[8.2px] leading-[1.4] tracking-[0.22em] font-medium text-text-primary pb-[2.34px]'>
                        EDUCATION
                      </div>
                      <div className='flex flex-col gap-[4.53px]'>
                        {education.map((edu) => (
                          <div key={edu.id} className='break-inside-avoid'>
                            <div className='flex flex-col gap-[2.34px] text-[5.86px] leading-[1.4] tracking-[0] font-regular text-text-primary'>
                              <div>
                                {edu.degree}, {edu.fieldOfStudy}
                              </div>
                              <div>{edu.schoolName}</div>
                              <div>{edu.studyPeriod}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </section>
                  )}

                  {/* Skills / Languages */}
                  {(language?.length > 0 || skill?.length > 0) && (
                    <section>
                      <div className='text-[8.2px] leading-[1.4] tracking-[0.22em] font-medium text-text-primary pb-[2.34px]'>
                        SKILLS
                      </div>
                      <div className='flex flex-col gap-[9.37px]'>
                        {language?.length > 0 && (
                          <div className='flex flex-col gap-[4.69px]'>
                            <div className='text-[5.86px] leading-[1.4] tracking-[0] font-regular text-text-primary'>
                              • Languages
                            </div>
                            {language.map((lang) => (
                              <div
                                key={lang.id}
                                className='flex text-[5.86px] leading-[1.4] tracking-[0] font-regular break-inside-avoid'
                              >
                                <div className='flex gap-[2.34px]'>
                                  <div className='min-w-[29.24px] text-text-primary flex-1'>
                                    {lang.language}
                                  </div>
                                  <div className='flex flex-col gap-[2.34px]'>
                                    <span className='text-text-primary'>
                                      {lang.proficiencyLevel}
                                    </span>
                                    <span className='text-text-secondary flex gap-[4.69px]'>
                                      {lang.test} {lang.score}
                                      <span className='text-grayscale-gray06'>
                                        {formatDate(lang.issueDate, {
                                          yearMonthOnly: true,
                                        })}
                                      </span>
                                    </span>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                        {skill?.length > 0 && (
                          <div className='flex flex-col gap-[4.69px] text-[5.86px] leading-[1.4] tracking-[0] font-regular text-text-primary'>
                            <div>• Technical</div>
                            <div className='flex flex-col gap-[2.34px]'>
                              {skill.map((tech, idx) => (
                                <div
                                  key={`${tech}-${idx}`}
                                  className='break-inside-avoid'
                                >
                                  {tech}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </section>
                  )}

                  {/* Links */}
                  {link?.length > 0 && (
                    <section>
                      <div className='text-[8.2px] leading-[1.4] tracking-[0.22em] font-medium text-text-primary pb-[2.34px]'>
                        LINKS
                      </div>
                      <div>
                        {link.map((l) => (
                          <div
                            key={l.id}
                            href={l.link}
                            target='_blank'
                            rel='noreferrer'
                            className='block text-[5.86px] break-words break-inside-avoid'
                          >
                            <span className='mr-[2.34px] flex-shrink-0'>•</span>
                            <span className='break-words flex-1 min-w-0'>
                              {l.link.replace('https://', '')}
                            </span>
                          </div>
                        ))}
                      </div>
                    </section>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  },
);

export default PreviewContent;
